/**
 * The player's own music, if they want it.
 *
 * The game ships with `public/music/` empty, so this whole path is inert by
 * default and the scheduler plays the synthesised pieces instead. Drop audio
 * files in there, list them in `public/music/index.txt` (or `index.json`), and
 * the same scheduler plays those instead — same silences between them, same
 * gating on tension, same ducking.
 *
 * A manifest rather than a directory listing, because a static server has no
 * way to enumerate a folder. Two formats are accepted: JSON, and one filename
 * per line, since the second is a good deal easier to write by hand.
 *
 * Everything here fails silently. A missing manifest, a malformed one, a file
 * the browser cannot decode, an element that refuses to play — all of them end
 * with `count === 0` and the procedural pieces carrying on as if this file did
 * not exist.
 */

import type { RNG } from '../core/types';

/** Relative to the page, so it survives being served from a subdirectory. */
const DIR = 'music/';
const MANIFESTS = [`${DIR}index.json`, `${DIR}index.txt`];

/** Sane ceiling. A manifest longer than this is a mistake, not a playlist. */
const MAX_TRACKS = 64;

/**
 * Resolves the manifest once per session, in the background. Probed as soon as
 * the audio graph is built, which is minutes before the first piece is due, so
 * by the time the scheduler asks the answer is already in.
 */
export class LocalTracks {
  private urls: readonly string[] = [];
  private probed = false;

  get count(): number {
    return this.urls.length;
  }

  /** Fire and forget. Safe to call more than once; only the first does work. */
  probe(): void {
    if (this.probed) return;
    this.probed = true;
    void this.load();
  }

  pick(rng: RNG): string | null {
    return this.urls.length === 0 ? null : rng.pick(this.urls);
  }

  private async load(): Promise<void> {
    for (const url of MANIFESTS) {
      const names = await this.read(url);
      if (names.length > 0) {
        this.urls = names;
        return;
      }
    }
  }

  private async read(url: string): Promise<string[]> {
    try {
      const res = await fetch(url, { cache: 'no-cache' });
      if (!res.ok) return [];
      const text = await res.text();
      // A dev server with an SPA fallback answers 200 with the page itself, so
      // the content has to be checked rather than the status.
      if (/^\s*</.test(text)) return [];
      const raw = url.endsWith('.json') ? parseJson(text) : parseLines(text);
      return raw
        .slice(0, MAX_TRACKS)
        .map((n) => n.trim())
        .filter((n) => n.length > 0 && !n.startsWith('#'))
        .map((n) => (/^(https?:)?\/\//.test(n) || n.includes('/') ? n : DIR + n));
    } catch {
      return [];
    }
  }
}

/** Accepts `["a.mp3"]` or `{ "tracks": ["a.mp3"] }`. */
function parseJson(text: string): string[] {
  try {
    const data: unknown = JSON.parse(text);
    const list = Array.isArray(data) ? data : (data as { tracks?: unknown })?.tracks;
    if (!Array.isArray(list)) return [];
    return list.filter((v): v is string => typeof v === 'string');
  } catch {
    return [];
  }
}

function parseLines(text: string): string[] {
  return text.split(/\r?\n/);
}

/**
 * One playing file, wrapped so the scheduler can treat it exactly like a
 * synthesised piece: it has a level to fade and a teardown.
 *
 * A `MediaElementAudioSourceNode` can only ever be built once per element and
 * cannot be re-pointed, so each track gets a fresh element and a fresh node,
 * both released on `dispose`.
 */
export class LocalTrack {
  /** Null when the browser would not give us a source node for the element. */
  readonly out: GainNode | null;

  private readonly el: HTMLAudioElement;
  private readonly src: MediaElementAudioSourceNode | null = null;

  constructor(ctx: AudioContext, url: string, dest: AudioNode) {
    this.el = new Audio();
    this.el.src = url;
    this.el.loop = false;
    this.el.preload = 'auto';
    this.el.crossOrigin = 'anonymous';

    let out: GainNode | null = null;
    try {
      this.src = ctx.createMediaElementSource(this.el);
      out = ctx.createGain();
      out.gain.value = 1;
      // Rolled off, but nothing like as hard as the synthesised pieces: this is
      // the player's own record and they chose to hear it. It still has to sit
      // under the hum, which is what the scheduler's level is for.
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 5200;
      lp.Q.value = 0.5;
      this.src.connect(lp);
      lp.connect(out);
      out.connect(dest);
    } catch {
      out = null;
    }
    this.out = out;
  }

  /** Rejects when autoplay policy is unhappy; there is nothing useful to do. */
  start(): void {
    void this.el.play().catch(() => undefined);
  }

  get ended(): boolean {
    return this.el.ended;
  }

  dispose(): void {
    try {
      this.el.pause();
    } catch {
      /* nothing to pause */
    }
    this.src?.disconnect();
    this.out?.disconnect();
    // Dropping the source lets the element release its buffer immediately
    // rather than whenever it is collected.
    this.el.removeAttribute('src');
    this.el.load();
  }
}
